import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Circle, ArrowRight, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function CourseDetail() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [enrolled, setEnrolled] = useState(false);

  useEffect(() => {
    api.get(`/courses/${courseId}`).then((r) => setCourse(r.data)).catch(() => {});
    if (user) {
      api.get(`/progress/${courseId}`).then((r) => setCompleted(r.data.completed_lesson_ids)).catch(() => {});
      api.get(`/enrollments`).then((r) => setEnrolled(r.data.some(e => e.course_id === courseId))).catch(() => {});
    }
  }, [courseId, user]);

  const enroll = async () => {
    if (!user) { toast.error("Please sign in to enroll"); return; }
    await api.post("/enrollments", { course_id: courseId });
    setEnrolled(true);
    toast.success(`Enrolled in ${course.title}`);
  };

  if (!course) return <div className="min-h-screen bg-[#0D0C0A] pt-32 px-6 text-[#B3AFA8]">Loading…</div>;

  return (
    <main data-testid="course-detail" className="bg-[#0D0C0A] min-h-screen pt-32 pb-24">
      <section className="relative h-[60vh] overflow-hidden">
        <img src={course.cover} alt={course.title} className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0C0A] via-[#0D0C0A]/50 to-transparent" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-16">
          <p className="label-eyebrow mb-5">{course.level} · {course.duration} · {course.lessons.length} lessons</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-5">{course.title}</h1>
          <p className="text-[#D4AF37] italic text-xl mb-5 max-w-2xl">{course.tagline}</p>
          <p className="text-[#D5D0C5] max-w-2xl text-lg leading-relaxed">{course.description}</p>
          <div className="mt-8 flex gap-4">
            {enrolled ? (
              <span data-testid="enrolled-badge" className="px-6 py-3 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] text-sm inline-flex items-center gap-2">
                <Check size={16} /> Enrolled
              </span>
            ) : (
              <button onClick={enroll} data-testid="enroll-btn" className="btn-gold px-8 py-4 rounded-full text-sm inline-flex items-center gap-2">
                <BookOpen size={16} /> Enroll in this class
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-12 mt-20">
        <p className="label-eyebrow mb-6">The lessons</p>
        <h2 className="font-display text-3xl sm:text-4xl mb-10">Step by step, hand by hand.</h2>
        <div className="space-y-1">
          {course.lessons.map((l, i) => {
            const done = completed.includes(l.id);
            return (
              <Link
                key={l.id}
                to={`/classes/${course.id}/${l.id}`}
                data-testid={`lesson-item-${l.id}`}
                className="group block border-t border-[#332D29] py-8 hover:bg-[#1A1715]/50 transition-colors px-4 -mx-4"
              >
                <div className="flex items-start gap-6">
                  <div className="pt-1">
                    {done ? <Check size={22} className="text-[#D4AF37]" /> : <Circle size={22} className="text-[#B3AFA8]/50" />}
                  </div>
                  <div className="flex-1">
                    <p className="label-eyebrow mb-2">Lesson 0{i+1}</p>
                    <h3 className="font-display text-2xl sm:text-3xl mb-2 group-hover:text-[#D4AF37] transition-colors">{l.title}</h3>
                    <p className="text-[#D5D0C5] leading-relaxed">{l.summary}</p>
                  </div>
                  <ArrowRight size={20} className="text-[#B3AFA8] group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all pt-1" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </main>
  );
}
